import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Builds the URL for backend API endpoints (communications, duplicate checks,
 * quote auth bootstrap, etc). By default the client calls the current origin's
 * /api routes so production can stay same-origin; override the base with
 * VITE_API_BASE_URL for environments that host the API elsewhere.
 */
export function getApiUrl(path: string): string {
  const viteEnv = import.meta.env as Record<string, string | boolean | undefined>;
  const configured = viteEnv.VITE_API_BASE_URL;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (typeof configured === 'string' && configured.length > 0) {
    return `${configured.replace(/\/$/, '')}${normalizedPath}`;
  }
  return `https://app.fast.repair${normalizedPath}`;
}

export function buildPublicQuoteLink(quoteId: string, accessToken: string): string {
  const params = new URLSearchParams({
    quoteId,
    accessToken,
  });
  return `https://fast.repair/viewQuote1?${params.toString()}`;
}

const EMBED_PASSTHROUGH_PARAMS = ['gclid', 'msclkid', 'fbclid', 'customerRef', 'appliance'] as const;

/**
 * Builds the src URL for the Fast Repair quote embed iframe.
 * Always includes embed=true and forwards known tracking/targeting parameters
 * from the current page URL into the iframe so they are captured by the form.
 */
export function buildEmbedSrc(extraParams?: Record<string, string>): string {
  const base = 'https://online-quote.fast.repair/';
  const params = new URLSearchParams({ embed: 'true' });

  const pageParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );

  for (const key of EMBED_PASSTHROUGH_PARAMS) {
    const value = pageParams.get(key);
    if (value) params.set(key, value);
  }

  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      if (value) params.set(key, value);
    }
  }

  return `${base}?${params.toString()}`;
}

export function extractPostcodePrefix(postcode: string): string {
  if (!postcode) return '';
  const cleanPostcode = postcode.trim().toUpperCase();
  const compactPostcode = cleanPostcode.replace(/\s+/g, '');
  
  const inwardMatch = compactPostcode.match(/^(.+)(\d[A-Z]{2})$/);
  if (inwardMatch) {
    return inwardMatch[1];
  }
  
  if (cleanPostcode.includes(' ')) {
    return cleanPostcode.split(' ')[0];
  }
  
  const fallbackMatch = compactPostcode.match(/^[A-Z]{1,2}[0-9][A-Z0-9]?/);
  return fallbackMatch ? fallbackMatch[0] : compactPostcode;
}
